import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Ticket, MapPin, Store as StoreIcon, Sparkles, Download, Wallet, Search, CheckCircle2, Clock, Info, MessageCircle } from "lucide-react";

const SUS_KEY = "cuponera_suscriptor_v1";
const PASOS = [
  "Guarda tu Cuponera: toca 'Instalar' o 'Agregar a inicio' en tu navegador.",
  "Explora cupones del comercio y de otros negocios cerca de tu C.P.",
  "Toca 'Quiero este cupón' y recibirás un código de 6 letras.",
  "Muestra el código al pagar en el comercio y disfruta tu descuento.",
];

export default function Cuponera() {
  const { codigo } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sus, setSus] = useState(() => { try { return JSON.parse(localStorage.getItem(SUS_KEY) || "null"); } catch { return null; } });
  const [tab, setTab] = useState("comercio");
  const [cp, setCp] = useState(sus?.codigo_postal || "");
  const [cpActivo, setCpActivo] = useState(sus?.codigo_postal || "");
  const [reclamos, setReclamos] = useState([]);
  const [installEvt, setInstallEvt] = useState(null);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "manifest"; link.href = `${process.env.REACT_APP_BACKEND_URL}/api/cuponera/${codigo}/manifest.json`;
    document.head.appendChild(link);
    const meta = document.createElement("meta"); meta.name = "theme-color"; meta.content = "#FF3366"; document.head.appendChild(meta);
    const onPrompt = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => { link.remove(); meta.remove(); window.removeEventListener("beforeinstallprompt", onPrompt); };
  }, [codigo]);

  useEffect(() => {
    const params = cpActivo ? { cp: cpActivo, rango: 10 } : {};
    api.get(`/cuponera/${codigo}`, { params }).then(r => setData(r.data)).catch(e => setError(formatApiError(e)));
  }, [codigo, cpActivo]);

  const loadReclamos = () => { if (sus?.id) api.get(`/cuponera/suscriptor/${sus.id}/cupones`).then(r => setReclamos(r.data.reclamos)).catch(() => {}); };
  useEffect(loadReclamos, [sus?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const reclamadosMap = useMemo(() => Object.fromEntries(reclamos.map(r => [r.cupon_id, r])), [reclamos]);

  const reclamar = async (c) => {
    if (!sus) { toast.error("Primero guarda tu celular y C.P."); return; }
    try {
      await api.post(`/cupones/${c.id}/reclamar`, { suscriptor_id: sus.id });
      toast.success("¡Cupón guardado en Mis cupones!");
      loadReclamos(); setTab("mios");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const instalar = async () => {
    if (installEvt) { installEvt.prompt(); setInstallEvt(null); return; }
    toast.info("En tu navegador toca ⋮ o 'Compartir' → 'Agregar a pantalla de inicio'");
  };

  if (error) return <Shell><div className="p-16 text-center text-white/60">{error}</div></Shell>;
  if (!data) return <Shell><div className="p-16 text-center text-white/60">Cargando cupones…</div></Shell>;

  const { tienda } = data;
  const hero = data.cupones_comercio[0];

  return (
    <Shell>
      {!sus && <Suscribir codigo={codigo} tienda={tienda} onDone={(s) => { setSus(s); setCp(s.codigo_postal); setCpActivo(s.codigo_postal); localStorage.setItem(SUS_KEY, JSON.stringify(s)); }} />}

      <header className="px-5 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#D4FF00] text-[11px] uppercase tracking-[0.25em] font-bold"><Ticket className="w-4 h-4" /> Cuponera</div>
          <button onClick={instalar} data-testid="cuponera-instalar" className="inline-flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full"><Download className="w-3.5 h-3.5" /> Instalar</button>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3 leading-[0.95]" data-testid="cuponera-tienda">{tienda.nombre_local}</h1>
        <div className="text-white/60 text-sm mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {[tienda.colonia, tienda.ciudad].filter(Boolean).join(", ") || tienda.categoria}{tienda.codigo_postal ? ` · C.P. ${tienda.codigo_postal}` : ""}</div>
        {sus && <div className="text-xs text-white/40 mt-1">Hola {sus.nombre || sus.telefono} · C.P. {sus.codigo_postal}</div>}
      </header>

      {hero && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="px-5">
          <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(255,51,102,0.6)]">
            <img src={hero.imagen_url} alt={hero.titulo} className="w-full aspect-[3/2] object-cover" />
            <div className="absolute top-3 left-3 bg-[#FF3366] text-white text-xs font-black px-3 py-1 rounded-full animate-pulse">🔥 {hero.descuento_texto}</div>
          </div>
        </motion.div>
      )}

      <nav className="px-5 mt-5 grid grid-cols-3 gap-1 bg-white/5 mx-5 rounded-full p-1 text-sm">
        {[["comercio", "Del comercio"], ["cerca", "Cerca de ti"], ["mios", `Mis cupones${reclamos.length ? ` (${reclamos.length})` : ""}`]].map(([k, l]) => (
          <button key={k} data-testid={`cuponera-tab-${k}`} onClick={() => setTab(k)} className={`h-10 rounded-full font-medium transition-colors ${tab === k ? "bg-white text-zinc-900" : "text-white/70"}`}>{l}</button>
        ))}
      </nav>

      <main className="px-5 py-5 space-y-4">
        {tab === "comercio" && (data.cupones_comercio.length ? data.cupones_comercio.map((c, i) => <CuponCard key={c.id} c={c} i={i} rec={reclamadosMap[c.id]} onClaim={() => reclamar(c)} />) : <Empty texto="Este comercio aún no tiene cupones activos. ¡Vuelve pronto!" />)}

        {tab === "cerca" && (
          <>
            <form onSubmit={e => { e.preventDefault(); if (cp.length === 5) setCpActivo(cp); else toast.error("C.P. de 5 dígitos"); }} className="bg-white/5 rounded-2xl p-4">
              <div className="text-sm font-medium flex items-center gap-2"><Search className="w-4 h-4 text-[#D4FF00]" /> Comercios cerca de tu domicilio</div>
              <div className="flex gap-2 mt-3">
                <Input data-testid="cuponera-cp" value={cp} onChange={e => setCp(e.target.value.replace(/\D/g, "").slice(0, 5))} inputMode="numeric" placeholder="Código postal" className="h-12 rounded-xl bg-white text-zinc-900 font-mono text-lg" />
                <Button type="submit" data-testid="cuponera-cp-buscar" className="h-12 rounded-xl bg-[#FF3366] hover:bg-[#E62958] px-5">Buscar</Button>
              </div>
            </form>
            {data.tiendas_cerca && (
              <div className="text-xs text-white/50" data-testid="cuponera-cerca-count">{data.tiendas_cerca.length} comercios afiliados cerca del C.P. {cpActivo}</div>
            )}
            {data.tiendas_cerca && data.tiendas_cerca.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {data.tiendas_cerca.map(t => (
                  <a key={t.id} href={t.codigo_cuponera ? `/cuponera/${t.codigo_cuponera}` : undefined} data-testid={`cerca-${t.id}`} className="shrink-0 bg-white/5 rounded-xl px-3 py-2 text-xs min-w-[150px]">
                    <div className="font-bold truncate flex items-center gap-1"><StoreIcon className="w-3 h-3 text-[#D4FF00]" /> {t.nombre_local}</div>
                    <div className="text-white/50">C.P. {t.codigo_postal} · {t.cupones} cupón{t.cupones === 1 ? "" : "es"}</div>
                  </a>
                ))}
              </div>
            )}
            {data.cupones_afiliados.length ? data.cupones_afiliados.map((c, i) => <CuponCard key={c.id} c={c} i={i} rec={reclamadosMap[c.id]} onClaim={() => reclamar(c)} showTienda />) : <Empty texto={cpActivo ? "No hay cupones de otros comercios cerca todavía." : "Escribe tu C.P. para ver cupones de comercios cercanos."} />}
          </>
        )}

        {tab === "mios" && (reclamos.length ? reclamos.map((r, i) => <CuponCard key={r.id} c={r.cupon} i={i} rec={r} showTienda />) : <Empty texto="Aún no has guardado cupones. ¡Toca 'Quiero este cupón'!" />)}

        <div className="bg-white/5 rounded-2xl p-4" data-testid="cuponera-pasos">
          <div className="text-sm font-bold flex items-center gap-2"><Info className="w-4 h-4 text-[#D4FF00]" /> ¿Cómo funciona tu Cuponera?</div>
          <ol className="mt-3 space-y-2 text-sm text-white/75">
            {PASOS.map((p, i) => <li key={`p-${i}`} className="flex gap-2"><span className="font-display font-black text-[#FF3366]">{i + 1}</span>{p}</li>)}
          </ol>
        </div>
        {tienda.whatsapp_gerente && (
          <a href={`https://wa.me/${(tienda.whatsapp_gerente || "").replace(/\D/g, "").replace(/^(\d{10})$/, "52$1")}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 rounded-2xl h-12 text-sm font-medium"><MessageCircle className="w-4 h-4" /> WhatsApp del comercio</a>
        )}
        <div className="text-center text-[10px] text-white/30 pb-6">Cuponera por en 90 MINUTOS · Comercios afiliados en todo México</div>
      </main>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-[#0B0B0F] text-white max-w-md mx-auto relative" style={{ fontFamily: "inherit" }}>{children}</div>;
}

function Empty({ texto }) {
  return <div className="border border-dashed border-white/15 rounded-2xl p-8 text-center text-sm text-white/50">{texto}</div>;
}

function CuponCard({ c, i, rec, onClaim, showTienda }) {
  const vigente = c.vigencia_fin >= new Date().toISOString().slice(0, 10);
  return (
    <motion.article initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.06, 0.4) }} data-testid={`pub-cupon-${c.id}`} className="rounded-2xl overflow-hidden bg-white text-zinc-900 shadow-xl">
      <img src={c.imagen_url} alt={c.titulo} className="w-full aspect-[3/2] object-cover" />
      <div className="p-4 relative">
        <div className="absolute -top-3 left-4 bg-[#D4FF00] text-zinc-900 text-xs font-black px-3 py-1 rounded-full">{c.descuento_texto}</div>
        {showTienda && c.tienda && <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1"><StoreIcon className="w-3 h-3" /> {c.tienda.nombre_local}{c.tienda.codigo_postal ? ` · C.P. ${c.tienda.codigo_postal}` : ""}</div>}
        <div className="font-display font-black text-xl tracking-tight mt-1">{c.titulo}</div>
        <div className="text-sm text-zinc-700">Aplica en: <b>{c.producto_aplica}</b></div>
        <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Vigente del {c.vigencia_inicio} al {c.vigencia_fin}</div>
        {c.condiciones && <div className="text-[11px] text-zinc-500 mt-1">Condiciones: {c.condiciones}</div>}
        {rec ? (
          <div className="mt-3 border-2 border-dashed border-zinc-300 rounded-xl p-3 text-center" data-testid={`codigo-${c.id}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{rec.canjeado ? "Cupón canjeado" : "Muestra este código al pagar"}</div>
            <div className={`font-mono font-black text-3xl tracking-[0.3em] mt-1 ${rec.canjeado ? "line-through text-zinc-400" : ""}`}>{rec.codigo}</div>
            {rec.canjeado && <div className="text-xs text-emerald-600 flex items-center justify-center gap-1 mt-1"><CheckCircle2 className="w-3.5 h-3.5" /> Usado</div>}
          </div>
        ) : (
          <Button onClick={onClaim} disabled={!vigente} data-testid={`reclamar-${c.id}`} className="mt-3 w-full h-12 rounded-xl bg-[#FF3366] hover:bg-[#E62958] font-display font-bold"><Sparkles className="w-4 h-4 mr-2" /> Quiero este cupón</Button>
        )}
      </div>
    </motion.article>
  );
}

function Suscribir({ codigo, tienda, onDone }) {
  const [tel, setTel] = useState("");
  const [cp, setCp] = useState("");
  const [nombre, setNombre] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post(`/cuponera/${codigo}/suscribir`, { telefono: tel, codigo_postal: cp, nombre });
      toast.success(data.nuevo ? "¡Bienvenido a tu Cuponera!" : "¡Qué bueno verte de nuevo!");
      onDone(data.suscriptor);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#0B0B0F]/95 backdrop-blur grid place-items-end sm:place-items-center p-4" data-testid="suscribir-modal">
      <motion.form initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onSubmit={submit} className="w-full max-w-md bg-white text-zinc-900 rounded-3xl p-6">
        <div className="w-12 h-12 rounded-2xl bg-[#FF3366] text-white grid place-items-center"><Wallet className="w-6 h-6" /></div>
        <h2 className="font-display font-black text-3xl tracking-tighter mt-4 leading-none">Activa tu Cuponera de {tienda.nombre_local}</h2>
        <p className="text-sm text-zinc-600 mt-2">Sin registro ni contraseña. Solo tu celular y código postal para enviarte cupones de este y otros comercios cercanos.</p>
        <div className="mt-4 space-y-3">
          <Input data-testid="sus-nombre" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Tu nombre (opcional)" className="h-12 rounded-xl" />
          <Input data-testid="sus-telefono" value={tel} onChange={e => setTel(e.target.value)} inputMode="tel" placeholder="Celular (10 dígitos)" required className="h-12 rounded-xl font-mono" />
          <Input data-testid="sus-cp" value={cp} onChange={e => setCp(e.target.value.replace(/\D/g, "").slice(0, 5))} inputMode="numeric" placeholder="Código postal" required className="h-12 rounded-xl font-mono" />
        </div>
        <Button type="submit" data-testid="sus-submit" disabled={loading} className="mt-4 w-full h-12 rounded-xl bg-[#FF3366] hover:bg-[#E62958] font-display font-bold">{loading ? "Activando…" : "Activar mi Cuponera"}</Button>
        <ol className="mt-4 space-y-1 text-[11px] text-zinc-500">
          {PASOS.map((p, i) => <li key={`sp-${i}`}>{i + 1}. {p}</li>)}
        </ol>
      </motion.form>
    </div>
  );
}
